import { SEARCH_FIELDS } from '@wyz/utils';
import { Matomo } from '~/common/helpers';
import { IBaseSearchFormSchema } from '~/purchase/types';

type SearchMode = 'quick' | 'manual' | 'none';

function resolveSearchMode(query: IBaseSearchFormSchema): SearchMode {
  if (query.identifier?.value || query.rawIdentifier) return 'quick';
  if (query.width && query.height && query.diameter) return 'manual';
  return 'none';
}

function formatDimensionLabel(query: IBaseSearchFormSchema): string {
  return `${query.width}/${query.height}R${query.diameter}`;
}

function resolveSearchCategory(
  mode: SearchMode,
  fieldNameSearch?: string | null,
): string {
  if (mode === 'manual') return Matomo.MATOMO_SEARCH_CATEGORY.DIMENSION_MANUAL;
  if (!fieldNameSearch) return Matomo.MATOMO_SEARCH_CATEGORY.UNKNOWN;
  if (fieldNameSearch === SEARCH_FIELDS.EAN)
    return Matomo.MATOMO_SEARCH_CATEGORY.EAN;
  if (fieldNameSearch === SEARCH_FIELDS.CAI)
    return Matomo.MATOMO_SEARCH_CATEGORY.CAI;
  return Matomo.MATOMO_SEARCH_CATEGORY.DIMENSION_QUICK;
}

function resolveSearchKeyword(
  query: IBaseSearchFormSchema,
  mode: SearchMode,
): string {
  if (mode === 'manual') return formatDimensionLabel(query);
  return query.identifier?.value || query.rawIdentifier || '';
}

export function trackSearchSubmitted(query: IBaseSearchFormSchema): void {
  const mode = resolveSearchMode(query);
  if (mode === 'none') return;

  const keyword = resolveSearchKeyword(query, mode);
  const category = resolveSearchCategory(mode, query.fieldNameSearch);

  Matomo.trackSiteSearch(keyword, category, false);
  Matomo.trackEvent(
    'PurchaseFunnel',
    Matomo.MATOMO_FUNNEL_STEP.SEARCH_SUBMITTED,
    mode,
  );

  if (mode === 'manual') {
    Matomo.trackEvent(
      'PurchaseSearch',
      'ManualDimensionSubmit',
      formatDimensionLabel(query),
    );
  } else {
    const fieldName = query.fieldNameSearch ?? 'unknown';
    const value = query.identifier?.value || query.rawIdentifier || '';
    Matomo.trackEvent(
      'PurchaseSearch',
      'QuickSearchSubmit',
      `${fieldName}:${value}`,
    );
  }
}

export function trackNewSearchClicked(query: IBaseSearchFormSchema): void {
  const mode = resolveSearchMode(query);
  if (mode === 'none') return;

  const keyword = resolveSearchKeyword(query, mode);
  const category = resolveSearchCategory(mode, query.fieldNameSearch);

  Matomo.trackEvent('PurchaseSearch', 'NewSearchClicked', category);
  Matomo.trackSiteSearch(keyword, category, false);
}

export function trackSearchNoResults(query: IBaseSearchFormSchema): void {
  const mode = resolveSearchMode(query);
  if (mode === 'none') return;

  const keyword = resolveSearchKeyword(query, mode);
  Matomo.trackEvent('PurchaseSearch', 'search_no_results', keyword);
}

export function trackSearchOnlyOutOfStock(query: IBaseSearchFormSchema): void {
  const mode = resolveSearchMode(query);
  if (mode === 'none') return;

  const keyword = resolveSearchKeyword(query, mode);
  Matomo.trackEvent('PurchaseSearch', 'search_only_out_of_stock', keyword);
}

export function trackAddedToCart(
  ean: string,
  dimension: string,
  brand: string,
  unitPrice: number,
  quantity: number,
  cartTotal: number,
): void {
  Matomo.addCartItem(ean, dimension, brand, unitPrice, quantity);
  Matomo.updateCartTotal(cartTotal);
  Matomo.trackEvent(
    'PurchaseFunnel',
    Matomo.MATOMO_FUNNEL_STEP.ADDED_TO_CART,
    ean,
    quantity,
  );
}

export function trackCartQuantityChanged(
  ean: string,
  newQuantity: number,
  unitPrice: number,
  brand: string,
  dimension: string,
  cartTotal: number,
): void {
  Matomo.addCartItem(ean, dimension, brand, unitPrice, newQuantity);
  Matomo.updateCartTotal(cartTotal);
  Matomo.trackEvent('PurchaseCart', 'QuantityChanged', ean, newQuantity);
}

export function trackCartItemRemoved(ean: string, cartTotal: number): void {
  Matomo.removeCartItem(ean);
  Matomo.updateCartTotal(cartTotal);
  Matomo.trackEvent('PurchaseCart', 'ItemRemoved', ean);
}

export function trackCartCleared(): void {
  Matomo.clearCart();
  Matomo.updateCartTotal(0);
  Matomo.trackEvent('PurchaseCart', 'CartCleared');
}

export function trackOrderConfirmed(
  references: string[],
  grandTotal: number,
  subTotal?: number,
  tax?: number,
  shipping?: number,
): void {
  const orderId = references.join(',');
  Matomo.trackOrder(orderId, grandTotal, subTotal, tax, shipping);
  Matomo.trackEvent(
    'PurchaseOrder',
    Matomo.MATOMO_FUNNEL_STEP.ORDER_CONFIRMED,
    orderId,
    Math.round(grandTotal),
  );
}

export function trackCheckoutDuration(durationMs: number): void {
  Matomo.trackEvent(
    'PurchaseOrder',
    'CheckoutDuration',
    '',
    Math.round(durationMs / 1000),
  );
}
